import { useState } from 'react';
import styles from './Interactive.module.css';

interface MultipleSelectConfig {
  options: string[];
  correctAnswers: string[];
}

interface MultipleSelectProps {
  config: MultipleSelectConfig;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  showResult: boolean;
}

export function MultipleSelect({ config, onSubmit, disabled, showResult }: MultipleSelectProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggleOption = (option: string) => {
    if (disabled) return;

    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (disabled || selected.size === 0) return;
    onSubmit(JSON.stringify(Array.from(selected)));
  };

  const getOptionClass = (option: string) => {
    let className = styles.checkboxOption;

    if (disabled) {
      className += ` ${styles.disabled}`;
    }

    if (showResult) {
      const isCorrect = config.correctAnswers.includes(option);
      const isSelected = selected.has(option);

      if (isCorrect && isSelected) {
        className += ` ${styles.correct}`;
      } else if (!isCorrect && isSelected) {
        className += ` ${styles.incorrect}`;
      } else if (isCorrect && !isSelected) {
        className += ` ${styles.missed}`;
      }
    } else if (selected.has(option)) {
      className += ` ${styles.selected}`;
    }

    return className;
  };

  return (
    <div className={styles.multipleSelectContainer}>
      {config.options.map((option, idx) => (
        <div
          key={idx}
          className={getOptionClass(option)}
          onClick={() => toggleOption(option)}
        >
          <div className={`${styles.checkbox} ${selected.has(option) ? styles.checked : ''}`}>
            {selected.has(option) && '✓'}
          </div>
          <span className={styles.checkboxLabel}>{option}</span>
        </div>
      ))}

      {!showResult && (
        <button
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={disabled || selected.size === 0}
        >
          Submit Answer
        </button>
      )}
    </div>
  );
}
